import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Canonical permission modules (mirrors backend PERMISSION_MODULES).
 * Keep in sync with /app/backend/routers/auth_users.py
 */
export const PERMISSION_MODULES = {
  kpis: ['dashboard', 'kpis', 'captura', 'calendario'],
  mesa_de_ayuda: ['dashboard', 'categorias', 'informes', 'logs_email', 'configuracion'],
  organigrama: null,
  base_de_conocimiento: null,
  capital_humano: ['organigrama', 'colaboradores', 'vacantes'],
  vigilancia: ['proveedores_visitas', 'herramentales', 'personal'],
};

const VALID_LEVELS = ['none', 'basic', 'full'];

// Per-module/submenu default levels (used when not explicitly stored).
// capital_humano.organigrama defaults to 'basic' for all users.
const DEFAULT_LEVELS = {
  capital_humano: { organigrama: 'basic' },
};

const defaultLevel = (module, sub) => {
  const modDef = DEFAULT_LEVELS[module];
  if (modDef && sub != null && modDef[sub]) return modDef[sub];
  return 'none';
};

/**
 * Returns permission helpers for the current user.
 *
 * Admin users always get "full" for everything.
 * User/Viewer users get their stored granular permissions (default: "none").
 *
 * API:
 *   getLevel(module, sub?)  -> "none" | "basic" | "full"
 *   canAccess(module, sub?) -> level !== "none"
 *   canWrite(module, sub?)  -> level === "full"
 *   canAccessModule(module) -> at least one sub (or single-level) != "none"
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Miembro del Equipo de Soporte (TI): actúa como admin SOLO dentro de Mesa de Ayuda.
  const isHelpdeskAgent = user?.helpdesk_is_agent === true;
  const isHelpdeskAdmin = isAdmin || isHelpdeskAgent;
  const permissions = user?.permissions || {};

  return useMemo(() => {
    const getLevel = (module, sub) => {
      if (isAdmin) return 'full';
      // La membresía del Equipo TI manda dentro de Mesa de Ayuda (por encima del granular)
      if (isHelpdeskAgent && module === 'mesa_de_ayuda') return 'full';
      const node = permissions[module];
      if (node == null) return defaultLevel(module, sub);
      // Single-level module (string)
      if (typeof node === 'string') {
        return VALID_LEVELS.includes(node) ? node : defaultLevel(module, sub);
      }
      // Multi-submenu module (dict)
      if (sub == null) return 'none';
      const val = node[sub];
      return VALID_LEVELS.includes(val) ? val : defaultLevel(module, sub);
    };

    const canAccess = (module, sub) => getLevel(module, sub) !== 'none';
    const canWrite = (module, sub) => getLevel(module, sub) === 'full';

    const canAccessModule = (module) => {
      if (isAdmin) return true;
      if (isHelpdeskAgent && module === 'mesa_de_ayuda') return true;
      const submenus = PERMISSION_MODULES[module];
      if (submenus === null) {
        return canAccess(module);
      }
      if (!submenus) return false;
      return submenus.some((s) => canAccess(module, s));
    };

    return {
      isAdmin,
      isHelpdeskAdmin,
      getLevel,
      canAccess,
      canWrite,
      canAccessModule,
    };
  }, [isAdmin, isHelpdeskAgent, isHelpdeskAdmin, permissions]);
};

export default usePermissions;
